import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import { FaCheckCircle, FaInfoCircle, FaTimesCircle } from "react-icons/fa";
import moment from "moment";
import PurchaseHeader from "./purchaseComponents/PurchaseHeader";
import ErrorPopup from "../../common/particles.jsx/ErrorPopup";
import { getSpPrintPbf, paymentCheckPbf } from "../../../services/purchaseApi";

// Invoice status 9: the order waits for the pharmacy's APJ to approve its SP.
const STATUS_WAITING_APJ_OUTLET = 9;
const PAYMENT_STATUS_PAID = 2;

/**
 * Result of a checkout, reached with a checkout group or invoice uid:
 * straight after a Tempo checkout, or after a CBD payment is confirmed.
 * `/purchase-pbf/checkout-result/failed` shows the failure, with the error
 * passed in router state.
 */
const CheckoutResult = () => {
  const { invoiceNum } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const failed = invoiceNum === "failed";

  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate: mutateCheck, isLoading } = useMutation({
    mutationFn: paymentCheckPbf,
    onSuccess: (data) => {
      if (!data) return;
      // CBD not paid yet: back to the payment page.
      if (
        data.payment_type === "cbd" &&
        data.payment_status !== PAYMENT_STATUS_PAID
      ) {
        navigate(`/purchase-pbf/confirm-checkout/${invoiceNum}`, {
          replace: true,
        });
        return;
      }
      // An invoice uid answers for that invoice only; ask again for the
      // whole checkout group so every invoice of this checkout is listed.
      if (!data.invoices?.length && data.checkout_group_uid) {
        mutateCheck({ checkout_group_uid: data.checkout_group_uid });
        return;
      }
      setResult(data);
    },
    onError: (err) =>
      setErrorMessage(err.message?.id ? err.message.id : err.message),
  });

  const { mutate: mutatePrintSp } = useMutation({
    mutationFn: getSpPrintPbf,
    onSuccess: (data, { invNum }) => {
      if (!data) return;
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SP OLIN ${invNum}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (err) =>
      setErrorMessage(err.message?.id ? err.message.id : err.message),
  });

  useEffect(() => {
    if (invoiceNum && !failed) mutateCheck({ invoice_uid: invoiceNum });
  }, [invoiceNum]);

  const invoices = result
    ? result.invoices?.length
      ? result.invoices
      : [
          {
            invoice_uid: result.invoice_uid,
            invoice_no: result.invoice_no,
            po_number: result.po_number,
            status: result.status,
          },
        ]
    : [];
  const waitingApj = invoices.some(
    (inv) => inv.status === STATUS_WAITING_APJ_OUTLET,
  );

  const buttons = (
    <div className="flex flex-col gap-3 mt-6">
      <div className="flex gap-3">
        <button
          type="button"
          className="flex-1 rounded-lg py-3 bg-[#EAF2FF] text-[#1D6EF0] hover:bg-blue-100"
          onClick={() => navigate("/purchase-pbf/cart")}
        >
          Ke Keranjang
        </button>
        {failed ? null : (
          <button
            type="button"
            className="flex-1 rounded-lg py-3 bg-[#1D6EF0] text-white hover:bg-blue-700"
            // One checkout can split into several transactions, so this
            // always opens the list; each Order ID opens its own detail.
            onClick={() => navigate("/purchase-pbf/list-transaction/0")}
          >
            Lihat Pesanan
          </button>
        )}
      </div>
      <button
        type="button"
        className="rounded-lg py-3 bg-white hover:bg-gray-100"
        onClick={() => navigate("/purchase-pbf")}
      >
        Ke Beranda
      </button>
    </div>
  );

  return (
    <div className="text-[12px] text-[#2C3E5D]">
      <PurchaseHeader />

      <div className="mt-[70px] px-3 pb-6 max-w-[520px] mx-auto">
        {failed ? (
          <div className="text-center pt-6">
            <p className="text-[22px] mb-6">Pesanan Gagal Dibuat</p>
            <FaTimesCircle className="text-[110px] text-red-500 mx-auto mb-6" />
            <p className="text-[14px] bg-white rounded-lg p-4">
              {state?.message || "Terjadi kesalahan saat membuat pesanan."}
            </p>
            {buttons}
          </div>
        ) : isLoading || !result ? (
          <div className="flex justify-center pt-16">
            <CircularProgress size={32} />
          </div>
        ) : (
          <div className="pt-6">
            <p className="text-[22px] mb-6 text-center">
              Pesanan Berhasil di Proses
            </p>
            <FaCheckCircle className="text-[110px] text-[#1D6EF0] mx-auto mb-6" />

            {invoices.map((inv) => (
              <div
                key={inv.invoice_uid}
                className="bg-[#EBEBEB] rounded-lg p-3 mb-3 text-[14px]"
              >
                <p className="text-gray-500">Tanggal Pesanan</p>
                <p className="mb-2">
                  {result.order_date
                    ? moment(result.order_date).format("DD MMMM YYYY")
                    : "-"}
                </p>
                <hr className="border-gray-400 mb-2" />
                {inv.label_group_name ? (
                  <p className="font-bold text-[16px] mb-2">
                    {inv.label_group_name}
                  </p>
                ) : null}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-500">Order ID</p>
                    {inv.invoice_uid ? (
                      <button
                        type="button"
                        className="text-[#1D6EF0] break-all text-left hover:underline"
                        onClick={() =>
                          navigate(
                            `/purchase-pbf/detail-transaction/${inv.invoice_uid}`,
                          )
                        }
                      >
                        {inv.invoice_no || inv.invoice_uid}
                      </button>
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500">Nomor SP/PO</p>
                    {inv.po_number ? (
                      <button
                        type="button"
                        className="text-[#1D6EF0] break-all text-left hover:underline"
                        onClick={() =>
                          mutatePrintSp({ invNum: inv.invoice_uid })
                        }
                      >
                        {inv.po_number}
                      </button>
                    ) : (
                      <p>-</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {waitingApj ? (
              <div className="flex items-start gap-3 bg-[#FFF8EB] text-[#F07B05] rounded-lg p-3 text-[14px]">
                <FaInfoCircle className="shrink-0 mt-1" />
                <p>
                  APJ perlu melakukan persetujuan pesanan SP digital di menu
                  Approval APJ
                </p>
              </div>
            ) : null}

            {buttons}
          </div>
        )}
      </div>

      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default CheckoutResult;
